import { RedirectToLogin, RequiredAuthProvider } from "@propelauth/react";
import { Toaster } from "sonner";
import {
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from "react-router-dom";
import HomePage from "./pages/home/HomePage";
import AccountDetailsPage from "./pages/home/account-details/AccountDetailsPage";
import AccountEvents from "./pages/home/account-details/account-events/AccountEvents";
import AccountOverview from "./pages/home/account-details/account-overview/AccountOverview";
import AccountTraits from "./pages/home/account-details/account-traits/AccountTraits";
import AccountTrends from "./pages/home/account-details/account-trends/AccountTrends";
import AccountUsers from "./pages/home/account-details/account-users/AccountUsers";
import AccountEmails from "./pages/home/account-details/account-emails/AccountEmails";
import AccountMeetings from "./pages/home/account-details/account-meetings/AccountMeetings";
import UserDetailsPage from "./pages/home/account-details/user-details/UserDetailsPage";
import UserActivity from "./pages/home/account-details/user-details/user-activity/UserActivity";
import UserEmails from "./pages/home/account-details/user-details/user-emails/UserEmails";
import UserMeetings from "./pages/home/account-details/user-details/user-meetings/UserMeetings";
import AccountsPage from "./pages/home/accounts/AccountsPage";
import GettingStartedPage from "./pages/home/getting-started/GettingStartedPage";
import EmailFirstGettingStarted from "./pages/home/getting-started/EmailFirstGettingStarted";
import HeadquartersPage from "./pages/home/headquarters/HeadquartersPage";
import InboxPage from "./pages/home/inbox/InboxPage";
import MeetingsPage from "./pages/home/meetings/MeetingsPage";
import IntegrationsPage from "./pages/home/settings/IntegrationsPage";
import EditPlaybookPage from "./pages/home/settings/playbooks/EditPlaybookPage";
import NewPlaybookPage from "./pages/home/settings/playbooks/NewPlaybookPage";
import PlaybooksPage from "./pages/home/settings/playbooks/PlaybooksPage";
import SignalsPage from "./pages/home/signals/SignalsPage";
import NewSignalPage from "./pages/home/signals/new-signal/NewSignalPage";
import SignalsOverviewPage from "./pages/home/signals/signal-overview/SignalsOverviewPage";
import AutomationsOverviewPage from "./pages/home/signals/signal-overview/AutomationsOverviewPage";
import NewAutomationPage from "./pages/home/signals/new-signal/NewAutomationPage";
import EditSignalPage from "./pages/home/signals/new-signal/EditSignalPage";
import SignalLogsPage from "./pages/home/signals/signal-log/SignalLogsPage";
import SignalAIBuilderPage from "./pages/home/signals/signal-ai-builder/SignalAIBuilderPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";
import AddIntegration from "./pages/onboarding/add-integration/AddIntegration";
import EmailSetupWizard from "./pages/onboarding/email-setup/EmailSetupWizard";
import NativeApiPage from "./pages/onboarding/native-api/NativeApiPage";
import OnboardingWorkspace from "./pages/onboarding/workspace/OnboardingWorkspace";
import Root from "./pages/root/RootPage";
import NewView from "./pages/viewer/NewView";
import Viewer from "./pages/viewer/Viewer";
import WorkspacesPage from "./pages/workspaces/WorkspacesPage";
import AuthProvider from "./utils/AuthProvider";
import SegmentApiPage from "./pages/onboarding/segment-api/SegmentApiPage";
import NotFoundImage from "./pages/error/NotFoundImage";
import SlackPage from "./pages/onboarding/slack/SlackPage";
import WorkspaceNotFoundPage from "./pages/error/WorkspaceNotFound";
import ExportsPage from "./pages/home/exports/ExportsPage";
import ExportDownloadHandler from "./pages/home/exports/ExportDownloadHandler";
import EntityDrawerWrapper from "./shared/drawer/EntityDrawerWrapper";
import HubspotPage from "./pages/onboarding/hubspot/HubspotPage";
import SalesforcePage from "./pages/onboarding/salesforce/SalesforcePage";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import GmailPage from "./pages/onboarding/gmail/GmailPage";
import GoogleCalendarPage from "./pages/onboarding/google-calendar/GoogleCalendarPage";
import DataManagementPage from "./pages/home/settings/data-management/DataManagementPage";
import ObjectsPage from "./pages/home/settings/data-management/objects/ObjectsPage";
import AccountCustomObjectTab from "./pages/home/account-details/account-custom-object/AccountCustomObjectTab";
import AccountDocuments from "./pages/home/account-details/account-documents/AccountDocuments";
import UserTraitsTab from "./pages/home/account-details/user-details/user-traits/UserTraitsTab";
import UserCustomObjectTab from "./pages/home/account-details/user-details/user-custom-object/UserCustomObjectTab";

import ComputedTraitsPage from "./pages/home/settings/data-management/computed-traits/ComputedTraitsPage";
import BusinessContextPage from "./pages/home/settings/business-context/BusinessContextPage";
import AccountSettingsHubPage from "./pages/home/settings/account-settings/AccountSettingsHubPage";
import WorkspaceSettingsHubPage from "./pages/home/settings/workspace-settings/WorkspaceSettingsHubPage";
import SettingsPage from "./pages/home/settings/SettingsPage";
import TeamsSettingsPage from "./pages/home/settings/teams/TeamsSettingsPage";
import ApiKeysSettingsPage from "./pages/home/settings/api-keys/ApiKeysSettingsPage";
import HealthScoreSettingsPage from "./pages/home/settings/health-score";
import HealthScoreModelBuilderPage from "./pages/home/settings/health-score/HealthScoreModelBuilderPage";
import SegmentsPage from "./pages/home/settings/segments/SegmentsPage";
import AlertsAssignmentPage from "./pages/home/settings/alerts-assignment/AlertsAssignmentPage";

import HealthPage from "./pages/home/health/HealthPage";
import { AIChatPage } from "./pages/home/ai-chat/AIChatPage";
import MeetingNoteTakerPage from "./pages/onboarding/meeting-note-taker/MeetingNoteTakerPage";
import SkillsPage from "./pages/home/skills/SkillsPage";
import SkillsListPage from "./pages/home/skills/skills-list/SkillsListPage";
import SkillEditorPage from "./pages/home/skills/skill-editor/SkillEditorPage";
import TasksPage from "./pages/home/tasks/TasksPage";
import TaskSettingsPage from "./pages/home/settings/tasks-settings/TaskSettingsPage";
import MeetingAutomationSettingsPage from "./pages/home/settings/meeting-automation/MeetingAutomationSettingsPage";
import EmailSettingsPage from "./pages/home/settings/email-settings/EmailSettingsPage";
import DomainReviewPage from "./pages/home/settings/domain-review/DomainReviewPage";
import AICreditsPage from "./pages/home/settings/ai-credits/AICreditsPage";
import McpConnectorsPage from "./pages/home/settings/integrations/McpConnectorsPage";

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<NotFoundImage />} element={<AuthProvider />}>
      <Route element={<EntityDrawerWrapper />}>
        <Route path="/workspace-not-found" element={<WorkspaceNotFoundPage />} />
        <Route path="/workspaces" element={<WorkspacesPage />} />
        <Route path="/viewer" element={<Viewer />} />
        <Route path="/new_view" element={<NewView />} />
        <Route path="/" element={<Root />}>
          <Route path="/onboarding" element={<OnboardingPage />}>
            <Route
              path="workspace/:workspaceId"
              element={<OnboardingWorkspace />}
            />
            <Route
              path="workspace/:workspaceId/integrations"
              element={<AddIntegration />}
            />
            <Route
              path="workspace/:workspaceId/email-setup"
              element={<EmailSetupWizard />}
            />
            <Route
              path="workspace/:workspaceId/integrations/hyperengage-api"
              element={<NativeApiPage />}
            />
            <Route
              path="workspace/:workspaceId/integrations/segment-api"
              element={<SegmentApiPage />}
            />
          </Route>
          <Route path="/home/:workspaceId" element={<HomePage />}>
            <Route path="getting-started" element={<GettingStartedPage />} />
            <Route path="get-started" element={<EmailFirstGettingStarted />} />
            <Route path="view/:viewId" element={<AccountsPage />} />
            <Route path="view/:viewId/new" element={<AccountsPage />} />
            <Route path="inbox" element={<InboxPage />} />
            <Route path="meetings" element={<MeetingsPage />} />
            <Route path="account/:accountId" element={<AccountDetailsPage />}>
              <Route path="overview" element={<AccountOverview />} />
              <Route path="traits" element={<AccountTraits />} />
              <Route path="users" element={<AccountUsers />} />
              <Route path="trends" element={<AccountTrends />} />
              <Route path="events" element={<AccountEvents />} />
              <Route path="emails" element={<AccountEmails />} />
              <Route path="calendar" element={<AccountMeetings />} />
              <Route path="docs" element={<AccountDocuments />} />
              <Route path="objects/:objectSlug" element={<AccountCustomObjectTab />} />
            </Route>
            <Route
              path="account/:accountId/user/:userId"
              element={<UserDetailsPage />}
            >
              <Route path="activity" element={<UserActivity />} />
              <Route path="traits" element={<UserTraitsTab />} />
              <Route path="emails" element={<UserEmails />} />
              <Route path="calendar" element={<UserMeetings />} />
              <Route path="objects/:objectSlug" element={<UserCustomObjectTab />} />
            </Route>
            <Route path="user/:userId" element={<UserDetailsPage />}>
              <Route path="activity" element={<UserActivity />} />
              <Route path="traits" element={<UserTraitsTab />} />
              <Route path="emails" element={<UserEmails />} />
              <Route path="calendar" element={<UserMeetings />} />
              <Route path="objects/:objectSlug" element={<UserCustomObjectTab />} />
            </Route>
            <Route
              path="view/:viewId/account/:accountId"
              element={<AccountDetailsPage />}
            >
              <Route path="overview" element={<AccountOverview />} />
              <Route path="traits" element={<AccountTraits />} />
              <Route path="users" element={<AccountUsers />} />
              <Route path="trends" element={<AccountTrends />} />
              <Route path="events" element={<AccountEvents />} />
              <Route path="emails" element={<AccountEmails />} />
              <Route path="calendar" element={<AccountMeetings />} />
              <Route path="docs" element={<AccountDocuments />} />
              <Route path="objects/:objectSlug" element={<AccountCustomObjectTab />} />
            </Route>
            <Route
              path="view/:viewId/account/:accountId/user/:userId"
              element={<UserDetailsPage />}
            >
              <Route path="activity" element={<UserActivity />} />
              <Route path="traits" element={<UserTraitsTab />} />
              <Route path="emails" element={<UserEmails />} />
              <Route path="calendar" element={<UserMeetings />} />
              <Route path="objects/:objectSlug" element={<UserCustomObjectTab />} />
            </Route>
            <Route
              path="view/:viewId/user/:userId"
              element={<UserDetailsPage />}
            >
              <Route path="activity" element={<UserActivity />} />
              <Route path="traits" element={<UserTraitsTab />} />
              <Route path="emails" element={<UserEmails />} />
              <Route path="calendar" element={<UserMeetings />} />
              <Route path="objects/:objectSlug" element={<UserCustomObjectTab />} />
            </Route>
            <Route path="headquarters" element={<HeadquartersPage />} />
            <Route path="signals" element={<SignalsPage />}>
              <Route index element={<SignalsOverviewPage />} />
              <Route path="overview" element={<SignalsOverviewPage />} />
              <Route path="automations" element={<AutomationsOverviewPage />} />
              <Route path="logs" element={<SignalLogsPage />} />
              <Route path="ai-builder" element={<SignalAIBuilderPage />} />
            </Route>

            <Route path="signals/new" element={<NewSignalPage />} />
            <Route path="signals/automations/new" element={<NewAutomationPage />} />
            <Route path="signals/:signalId/edit" element={<EditSignalPage />} />
            <Route path="skills" element={<SkillsPage />}>
              <Route index element={<SkillsListPage />} />
              <Route path="new" element={<SkillEditorPage />} />
              <Route path=":skillId" element={<SkillEditorPage />} />
            </Route>
            <Route path="ai-chat" element={<AIChatPage />} />
            <Route path="health" element={<HealthPage />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="exports" element={<ExportsPage />} />
          </Route>
          <Route path="/home/:workspaceId/settings" element={<SettingsPage />}>
            <Route index element={<IntegrationsPage />} />
            <Route path="integrations" element={<IntegrationsPage />} />
            <Route path="integrations/slack" element={<SlackPage />} />
            <Route path="integrations/hubspot" element={<HubspotPage />} />
            <Route path="integrations/salesforce" element={<SalesforcePage />} />
            <Route path="integrations/gmail" element={<GmailPage />} />
            <Route path="integrations/google-calendar" element={<GoogleCalendarPage />} />
            <Route path="integrations/meeting-note-taker" element={<MeetingNoteTakerPage />} />
            <Route path="integrations/sdk-setup" element={<NativeApiPage />} />
            <Route path="integrations/segment-setup" element={<SegmentApiPage />} />
            <Route path="integrations/mcp" element={<McpConnectorsPage />} />
            <Route path="playbooks" element={<PlaybooksPage />} />
            <Route
              path="playbooks/:playbookId/edit"
              element={<EditPlaybookPage />}
            />
            <Route path="playbooks/new" element={<NewPlaybookPage />} />
            <Route path="computed-traits" element={<ComputedTraitsPage />} />
            <Route path="data-management/metrics" element={<ComputedTraitsPage />} />
            <Route path="data-management" element={<DataManagementPage />}>
              <Route index element={<ObjectsPage />} />
              <Route path="objects" element={<ObjectsPage />} />
            </Route>
            <Route path="business-context" element={<BusinessContextPage />} />
            <Route path="account" element={<AccountSettingsHubPage />} />
            <Route path="security" element={<AccountSettingsHubPage initialTab="security" />} />
            <Route path="workspace" element={<WorkspaceSettingsHubPage />} />
            <Route path="members" element={<WorkspaceSettingsHubPage initialTab="members" />} />
            <Route path="teams" element={<TeamsSettingsPage />} />
            <Route path="api-keys" element={<ApiKeysSettingsPage />} />
            <Route path="segments" element={<SegmentsPage />} />
            <Route path="health-score" element={<HealthScoreSettingsPage />} />
            <Route path="health-score/model/:scopeKey" element={<HealthScoreModelBuilderPage />} />
            <Route path="alerts-assignment" element={<AlertsAssignmentPage />} />
            <Route path="tasks" element={<TaskSettingsPage />} />
            <Route path="meeting-automation" element={<MeetingAutomationSettingsPage />} />
            <Route path="email-settings" element={<EmailSettingsPage />} />
            <Route path="domain-review" element={<DomainReviewPage />} />
            <Route path="ai-credits" element={<AICreditsPage />} />
          </Route>
          <Route path="/exports/:workspaceId/:exportId/download" element={<ExportDownloadHandler />} />
        </Route>
      </Route>
    </Route>
  )
);

const Main = () => (
  <div className="w-screen h-screen overflow-auto">
    <RequiredAuthProvider
      authUrl={import.meta.env.VITE_AUTH_URL!}
      displayIfLoggedOut={
        <RedirectToLogin postLoginRedirectUrl={window.location.href} />
      }
      displayWhileLoading={
        <div className="flex h-screen w-screen items-center justify-center bg-background">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary/25 border-t-primary" />
        </div>
      }
    >
      <RouterProvider router={router} />
      <Toaster position="bottom-right" richColors closeButton />
    </RequiredAuthProvider>
  </div>
);

export default Main;
